import { Toast } from "@heroui/react";
import { useCallback, useMemo } from "react";

import { useTranslations } from "@/src/i18n/i18n";
import { apiErrorMessage } from "@/src/libs/apiErrorMessage";

export type ShowAppToast = (_label: string, _description?: string) => void;

export type AppToast = {
  showError: ShowAppToast;
  showSuccess: ShowAppToast;
  showWarning: ShowAppToast;
  /** Toast d'erreur dont la description est déduite de l'erreur d'API. */
  showApiError: (_label: string, _error: unknown) => void;
};

/**
 * Une seule façon d'afficher un toast.
 *
 * Le `toast` de HeroUI v3 est un singleton impératif — pas de provider, pas de
 * hook obligatoire. Ce wrapper existe quand même pour deux raisons :
 *
 * 1. **un seul endroit dépend de HeroUI**, donc un seul endroit à toucher si la
 *    lib change (c'est ce qui a coûté 72 blocs recopiés côté app) ;
 * 2. **`showApiError` sait lire une `ApiError`**, la classer (cf.
 *    `apiErrorMessage`) et la dire dans la langue courante.
 *
 * ⚠️ Le `<Toast.Provider />` doit être monté une fois dans l'arbre, sans quoi
 * rien ne s'affiche.
 */
export function useAppToast(): AppToast {
  const t = useTranslations("errors");

  /**
   * Message lisible pour une erreur d'API.
   *
   * Dépend de la langue, donc de `t` — c'est pourquoi il vit dans le hook et
   * non à côté : un helper de module figerait la langue au chargement.
   */
  const describeApiError = useCallback(
    (error: unknown) => {
      const message = apiErrorMessage(error);
      return "key" in message ? t(message.key) : message.code;
    },
    [t],
  );

  return useMemo<AppToast>(
    () => ({
      showError: (label, description) =>
        Toast.toast.danger(label, { description }),
      showSuccess: (label, description) =>
        Toast.toast.success(label, { description }),
      showWarning: (label, description) =>
        Toast.toast.warning(label, { description }),
      showApiError: (label, error) =>
        Toast.toast.danger(label, { description: describeApiError(error) }),
    }),
    [describeApiError],
  );
}
